import type { AssignableRole, InvitationInfo, MemberInfo, OrganizationDetail } from '@viewer/protocol';
import { useState } from 'react';
import { api, errorText } from '../../lib/api/client';
import { useApi } from '../../lib/api/useApi';
import { Modal } from '../../ui/Modal';
import { MemberAccessDialog } from './MemberAccessDialog';
import { formatDateTime, invitationStatusLabel, roleLabel } from './labels';
import styles from './org.module.css';

interface Props {
  org: OrganizationDetail;
  userId: string;
  onChanged(): Promise<void>;
}

/** Quem o usuário atual pode gerenciar: mesma regra do servidor (Admin só mexe em Membros). */
function canManage(actorRole: OrganizationDetail['role'], target: MemberInfo, userId: string) {
  if (target.userId === userId || target.role === 'owner') return false;
  if (actorRole === 'owner') return true;
  return actorRole === 'admin' && target.role === 'member';
}

export function MembersTab({ org, userId, onChanged }: Props) {
  const members = useApi<MemberInfo[]>(`/api/orgs/${org.id}/members`);
  const isStaff = org.role !== 'member';
  const invitations = useApi<InvitationInfo[]>(isStaff ? `/api/orgs/${org.id}/invitations` : null);
  const [inviting, setInviting] = useState(false);
  const [editing, setEditing] = useState<MemberInfo | null>(null);
  const [error, setError] = useState<string | null>(null);

  async function run(action: () => Promise<unknown>) {
    setError(null);
    try {
      await action();
      await Promise.all([members.reload(), invitations.reload(), onChanged()]);
    } catch (err) {
      setError(errorText(err));
    }
  }

  function remove(member: MemberInfo) {
    if (confirm(`Remover ${member.name} da empresa? Sessões abertas dessa pessoa serão encerradas.`)) {
      void run(() => api.delete(`/api/orgs/${org.id}/members/${member.userId}`));
    }
  }

  const pending = invitations.data?.filter((i) => i.status === 'pending') ?? [];

  return (
    <>
      <section className={styles.panel}>
        <div className={styles.panelHead}>
          <h2 className={styles.panelTitle}>Colaboradores</h2>
          {isStaff && (
            <button type="button" className={`${styles.btn} ${styles.btnPrimary}`} onClick={() => setInviting(true)}>
              + Convidar
            </button>
          )}
        </div>
        {error && <p className={styles.error}>{error}</p>}
        {members.error && <p className={styles.error}>{members.error}</p>}
        <div className={styles.list}>
          {members.data?.map((member) => (
            <div key={member.userId} className={styles.row}>
              <div className={styles.rowMain}>
                <span className={styles.rowName}>
                  {member.name}
                  {member.userId === userId && <span className={styles.badge}>você</span>}
                </span>
                <span className={styles.rowMeta}>{member.email}</span>
              </div>
              <span className={`${styles.badge} ${member.role === 'owner' ? styles.badgeAccent : ''}`}>{roleLabel[member.role]}</span>
              {canManage(org.role, member, userId) && (
                <div className={styles.rowActions}>
                  <button type="button" className={`${styles.btn} ${styles.btnSecondary} ${styles.btnSmall}`} onClick={() => setEditing(member)}>
                    Permissões
                  </button>
                  <button type="button" className={`${styles.btn} ${styles.btnDanger} ${styles.btnSmall}`} onClick={() => remove(member)}>
                    Remover
                  </button>
                </div>
              )}
            </div>
          ))}
        </div>
      </section>

      {isStaff && (
        <section className={styles.panel}>
          <div className={styles.panelHead}>
            <h2 className={styles.panelTitle}>Convites pendentes</h2>
          </div>
          {pending.length === 0 && <p className={styles.empty}>Nenhum convite aguardando resposta.</p>}
          <div className={styles.list}>
            {pending.map((invite) => (
              <div key={invite.id} className={styles.row}>
                <div className={styles.rowMain}>
                  <span className={styles.rowName}>{invite.email}</span>
                  <span className={styles.rowMeta}>
                    {roleLabel[invite.role]} · {invitationStatusLabel[invite.status]} · vence {formatDateTime(invite.expiresAt)}
                  </span>
                </div>
                <div className={styles.rowActions}>
                  <button
                    type="button"
                    className={`${styles.btn} ${styles.btnDanger} ${styles.btnSmall}`}
                    onClick={() => void run(() => api.delete(`/api/orgs/${org.id}/invitations/${invite.id}`))}
                  >
                    Revogar
                  </button>
                </div>
              </div>
            ))}
          </div>
        </section>
      )}

      {inviting && (
        <InviteDialog
          org={org}
          onClose={() => setInviting(false)}
          onCreated={() => void Promise.all([invitations.reload(), onChanged()])}
        />
      )}
      {editing && (
        <MemberAccessDialog
          org={org}
          member={editing}
          onClose={() => setEditing(null)}
          onSaved={() => void run(async () => {})}
        />
      )}
    </>
  );
}

function InviteDialog({ org, onClose, onCreated }: { org: OrganizationDetail; onClose(): void; onCreated(): void }) {
  const [email, setEmail] = useState('');
  const [role, setRole] = useState<AssignableRole>('member');
  const [link, setLink] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function create() {
    setBusy(true);
    setError(null);
    try {
      const invite = await api.post<InvitationInfo>(`/api/orgs/${org.id}/invitations`, { email: email.trim(), role });
      setLink(invite.link ?? null);
      onCreated();
    } catch (err) {
      setError(errorText(err));
    } finally {
      setBusy(false);
    }
  }

  async function copy() {
    if (!link) return;
    await navigator.clipboard.writeText(link);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  }

  return (
    <Modal title="Convidar colaborador" eyebrow={org.name} onClose={onClose}>
      {link ? (
        <div className={styles.form}>
          <p className={styles.help}>
            Envie este link para <strong>{email}</strong>. Ele vale por 7 dias e só funciona para quem entrar com esse
            e-mail. Depois de aceito, não pode ser reaproveitado.
          </p>
          <div className={styles.linkBox}>{link}</div>
          <div className={styles.actions}>
            <button type="button" className={`${styles.btn} ${styles.btnSecondary}`} onClick={() => void copy()}>
              {copied ? 'Copiado!' : 'Copiar link'}
            </button>
            <button type="button" className={`${styles.btn} ${styles.btnPrimary}`} onClick={onClose}>
              Concluído
            </button>
          </div>
        </div>
      ) : (
        <form
          className={styles.form}
          onSubmit={(e) => {
            e.preventDefault();
            void create();
          }}
        >
          <label className={styles.field}>
            <span className={styles.label}>E-mail</span>
            <input className={styles.input} type="email" autoFocus required value={email} onChange={(e) => setEmail(e.target.value)} />
          </label>
          <label className={styles.field}>
            <span className={styles.label}>Função</span>
            <select className={styles.select} value={role} onChange={(e) => setRole(e.target.value as AssignableRole)}>
              <option value="member">{roleLabel.member} — acessa só o que for liberado</option>
              {org.role === 'owner' && <option value="admin">{roleLabel.admin} — organiza grupos, dispositivos e membros</option>}
            </select>
          </label>
          {error && <p className={styles.error}>{error}</p>}
          <div className={styles.actions}>
            <button type="button" className={`${styles.btn} ${styles.btnSecondary}`} onClick={onClose} disabled={busy}>
              Cancelar
            </button>
            <button type="submit" className={`${styles.btn} ${styles.btnPrimary}`} disabled={busy || !email.trim()}>
              {busy ? 'Gerando...' : 'Gerar link de convite'}
            </button>
          </div>
        </form>
      )}
    </Modal>
  );
}
